/**
 * platform predefined colors
 */
export const colors = {
  dark_slate: '#415363',
  medium_slate: '#758491',
  specialty_teal: '#00748C',
  green: '#1E978A',
  pale_blue: '#4D9CB9',
  gold: '#D7A362',
  capital_blue: '#1D9AD6',
  light_gray: '#A3BAC2',
  agency_red: '#DF2726',
};

export const colorsBySourceAgencyKey = new Map<string, string>([
  ['SPE', colors.specialty_teal],
  ['CNR', colors.pale_blue],
  ['CAH', colors.agency_red],
  ['DPK', colors.capital_blue],
]);

export const defaultChartColors = [
  colors.dark_slate,
  colors.pale_blue,
  colors.specialty_teal,
  colors.green,
  colors.medium_slate,
  colors.gold,
  colors.capital_blue,
  colors.light_gray,
];

export const bubbleChartColors = [
  '#A3BAC2',
  '#77c2e6',
  '#177bab',
  '#092e40',
  '#000000'
]

export const multiSeriesColors = defaultChartColors.slice(0, 2).join(',');
export const triSeriesColors = defaultChartColors.slice(0, 3);
export const barChartColors = defaultChartColors.slice(0, 1);
export const chartPrimaryColor = defaultChartColors[0];
export const chartSecondaryColor = defaultChartColors[1];
export const chartTertiaryColor = defaultChartColors[2];
export const chartQuaternaryColor = defaultChartColors[0];
export const chartHighlightColor = chartSecondaryColor;
export const chartMutedColor = '#999999';
export const chipColor = '#4E738A';
export const agencyChipColors = {
  Agency: '#DF2726',
  Specialty: '#00748D',
  Capital: '#1D9AD6',
}
export const brokerPaletteColors = ['#1D9AD6', '#1E978A', '#00748C', '#DF2726'];
export const paletteColors = defaultChartColors;
export const stackedSeriesColors = defaultChartColors.join(',');

export const vibrantColorScale = [
  '#b7094c',
  '#a01a58',
  '#892b64',
  '#723c70',
  '#5c4d7d',
  '#455e89',
  '#2e6f95',
  '#1780a1',
  '#0091ad',
];

export const colorScale = new Map<string, string>([
  ['scale1', '#66BF7C'],
  ['scale2', '#7AC57D'],
  ['scale3', '#BAD780'],
  ['scale4', '#FEE382'],
  ['scale5', '#FBB379'],
  ['deltaRed', '#ffcdc5'],
  ['deltaGreen', '#e7ffd6'],
  ['scaleGray', '#bababa'],
]);

interface Chip {
  bgColor: string;
  textColor: string;
}

export const chipColors: Chip[] = [
  { bgColor: 'tw:bg-secondary', textColor: 'tw:text-white' },
  { bgColor: 'tw:bg-gold', textColor: 'tw:text-white' },
  { bgColor: 'tw:bg-company-green', textColor: 'tw:text-white' },
];

export const colorScaleFromValue = (
  value: number,
  max: number,
  min: number,
  baseColor = 'scale',
  scale = 6,
): string => {
  const diff = max - min;
  const interval = diff / scale;
  let n = max;
  let x = scale;
  for (let i = 1; i <= scale; i++) {
    if (value >= n) {
      x = i;
      break;
    }
    n -= interval;
  }
  // return `var(--v-${baseColor}${x}-base)`;
  return colorScale.get(`${baseColor}${x}`) || '';
};

type compactorFn = (value: number | null | undefined | string, levels?: string[]) => string;

export const vibrantColorScaleRanges = (
  max: number,
  min: number,
  compactor: compactorFn,
  compactLevel?: string[],
): { min: string; max: string; color: string }[] => {
  if (Math.abs(max) === Infinity || Math.abs(min) === Infinity) {
    return [];
  }
  if (max === min) {
    return [{ max: compactor(max, compactLevel), min: compactor(min, compactLevel), color: vibrantColorScale[0] }];
  }
  const reversedScale = vibrantColorScale.slice().reverse();
  const scale = vibrantColorScale.length;
  const diff = max - min;
  const interval = diff / scale;
  const ranges: { min: string; max: string; color: string }[] = [];
  for (let i = 0; i < scale; i++) {
    const rangeMin = i * interval + min;
    const rangeMax = rangeMin + interval;
    ranges.push({
      min: compactor(rangeMin, compactLevel),
      max: compactor(rangeMax, compactLevel),
      color: reversedScale[i],
    });
  }
  return ranges;
};

export const vibrantColorScaleFromValue = (value: number, max: number, min: number): string => {
  const scale = vibrantColorScale.length;
  const diff = max - min;
  const interval = diff / scale;
  let n = max;
  let x = scale;
  for (let i = 1; i <= scale; i++) {
    if (value >= n) {
      x = i;
      break;
    }
    n -= interval;
  }
  return vibrantColorScale[x - 1];
};

export const colorFromAbbrev = (text: string | undefined): string => {
  if (!text) {
    return brokerPaletteColors[brokerPaletteColors.length - 1];
  }
  const n = parseInt(
    text
      .split('')
      .map((char) => char.charCodeAt(0))
      .join(''),
    10,
  );
  return brokerPaletteColors[n % brokerPaletteColors.length];
};

export const adjustColor = (hexColor: string, by: number): string => {
  const col = hexColor.slice(1); // remove the #

  const num = parseInt(col, 16);
  let r = (num >> 16) + by;
  if (r > 255) {
    r = 255;
  } else if (r < 0) {
    r = 0;
  }

  let b = ((num >> 8) & 0x00ff) + by;
  if (b > 255) {
    b = 255;
  } else if (b < 0) {
    b = 0;
  }

  let g = (num & 0x0000ff) + by;
  if (g > 255) {
    g = 255;
  } else if (g < 0) {
    g = 0;
  }

  return '#' + (g | (b << 8) | (r << 16)).toString(16);
};
